import i18n from 'i18next'
import { getCurrentLanguage } from '@/i18n'

// Default time constants
export const DEFAULT_START_TIME = '09:00'
export const DEFAULT_END_TIME = '10:00'

// Type for translation function
type TFunction = (key: string, options?: { count?: number }) => string

/**
 * Parses a date string as UTC, handling various formats and edge cases
 * @param dateStr - The date string to parse
 * @returns A Date object in UTC
 * @throws Error if the date string is invalid
 */
export function parseAsUTC(dateStr: string): Date {
  // If there's no timezone at the end, assume UTC:
  const iso = /[Zz]|[+-]\d{2}(:\d{2})?$/.test(dateStr) ? dateStr : dateStr + 'Z'

  // Some engines choke on >3‐digit fractions, so truncate to milliseconds:
  const normalized = iso.replace(/(\.\d{3})\d+/, '$1')

  const d = new Date(normalized)
  if (isNaN(d.getTime())) {
    throw new Error(`Invalid date: ${dateStr}`)
  }
  return d
}

/**
 * Formats a date to show relative time (e.g., "3 mins ago", "2 hours ago")
 * @param date - The date to format
 * @param t - Translation function (optional, uses i18n if not provided)
 * @returns A string representing the relative time
 */
export const formatRelativeTime = (
  date: Date | string,
  t?: TFunction
): string => {
  const now = new Date()
  const targetDate = typeof date === 'string' ? new Date(date) : date
  const diffInSeconds = Math.floor(
    (now.getTime() - targetDate.getTime()) / 1000
  )

  // Use provided translation function or fallback to i18n
  const translate =
    t ||
    ((key: string, options?: { count?: number }) =>
      i18n.t(`common:time.${key}` as never, options))

  if (diffInSeconds < 60) {
    return translate('just_now')
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return translate(diffInMinutes === 1 ? 'minute_ago' : 'minutes_ago', {
      count: diffInMinutes,
    })
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return translate(diffInHours === 1 ? 'hour_ago' : 'hours_ago', {
      count: diffInHours,
    })
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return translate(diffInDays === 1 ? 'day_ago' : 'days_ago', {
      count: diffInDays,
    })
  }

  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 4) {
    return translate(diffInWeeks === 1 ? 'week_ago' : 'weeks_ago', {
      count: diffInWeeks,
    })
  }

  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return translate(diffInMonths === 1 ? 'month_ago' : 'months_ago', {
      count: diffInMonths,
    })
  }

  const diffInYears = Math.floor(diffInDays / 365)
  return translate(diffInYears === 1 ? 'year_ago' : 'years_ago', {
    count: diffInYears,
  })
}

/**
 * Formats a date string with smart relative time display
 * @param dateStr - The date string to format
 * @param t - Translation function (optional, uses i18n if not provided)
 * @returns A string representing the relative time or formatted date
 */
export function timeAgo(dateStr: string, t?: TFunction): string {
  const date = parseAsUTC(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)
  const diffMinutes = diffMs / (1000 * 60)

  // Use provided translation function or fallback to i18n
  const translate =
    t ||
    ((key: string, options?: { count?: number }) =>
      i18n.t(`common:time.${key}` as never, options))

  if (diffHours >= 36) {
    // More than 36 hours ago: return the full date and time in locale format.
    return formatDateTime(dateStr)
  } else if (diffHours >= 24) {
    // More than 24 hours (but less than 36 hours) ago: "A day ago"
    return translate('a_day_ago')
  } else if (diffHours >= 2) {
    // More than 2 hours ago: return "N hours ago"
    return translate('hours_ago', { count: Math.floor(diffHours) })
  } else if (diffHours >= 1) {
    // More than 1 hour (but less than 2 hours) ago: "Just over an hour ago"
    return translate('just_over_an_hour_ago')
  } else if (diffMinutes >= 2) {
    // More than 2 minutes ago: return "N minutes ago"
    return translate('minutes_ago', { count: Math.floor(diffMinutes) })
  } else {
    // Otherwise, it's just now.
    return translate('just_now')
  }
}

/**
 * Formats a date to a readable format (e.g., "Jan 15, 2024")
 * @param date - The date to format (can be Date object or string)
 * @param locale - The locale to use for formatting (default: current i18n locale)
 * @returns A string representing the formatted date
 */
export const formatDate = (date: Date | string, locale?: string): string => {
  if (!date) return 'N/A'

  const targetDate = typeof date === 'string' ? new Date(date) : date
  const currentLocale = locale || getCurrentLanguage()

  return targetDate.toLocaleDateString(currentLocale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Formats a date to include time (e.g., "Jan 15, 2024 · 14:30")
 * @param date - The date to format (can be Date object or string)
 * @param locale - The locale to use for formatting (default: current i18n locale)
 * @param hour12 - Whether to use 12-hour format (default: false for 24-hour format)
 * @returns A string representing the formatted date with time
 */
export const formatDateTime = (
  date: Date | string,
  locale?: string,
  hour12 = false
): string => {
  if (!date) return 'N/A'

  const targetDate = typeof date === 'string' ? new Date(date) : date
  const currentLocale = locale || getCurrentLanguage()

  const dateFormatter = new Intl.DateTimeFormat(currentLocale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const timeFormatter = new Intl.DateTimeFormat(currentLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  })

  return `${dateFormatter.format(targetDate)} · ${timeFormatter.format(
    targetDate
  )}`
}

/**
 * Formats a date to show only the time (e.g., "14:30")
 * @param date - The date to format (can be Date object or string)
 * @param locale - The locale to use for formatting (default: current i18n locale)
 * @param hour12 - Whether to use 12-hour format (default: false for 24-hour format)
 * @returns A string representing the formatted time
 */
export const formatTime = (date: Date | string, locale?: string, hour12 = false): string => {
  if (!date) return 'N/A'

  const targetDate = typeof date === 'string' ? new Date(date) : date
  const currentLocale = locale || getCurrentLanguage()

  return targetDate.toLocaleTimeString(currentLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  })
}

/**
 * Validates that end time is after start time
 * @param startTime - Start time in HH:MM format
 * @param endTime - End time in HH:MM format
 * @param t - Translation function (optional, uses i18n if not provided)
 * @returns Object with isValid boolean and error message if invalid
 */
export const validateTime = (
  startTime: string,
  endTime: string,
  t?: TFunction
): { isValid: boolean; error?: string } => {
  if (!startTime || !endTime) {
    return { isValid: true }
  }

  const [startHour, startMinute] = startTime.split(':').map(Number)
  const [endHour, endMinute] = endTime.split(':').map(Number)

  const startMinutes = startHour * 60 + startMinute
  const endMinutes = endHour * 60 + endMinute

  if (endMinutes <= startMinutes) {
    // Use provided translation function or fallback to i18n
    const translate =
      t || ((key: string) => i18n.t(`common:time.${key}` as never))

    return {
      isValid: false,
      error: translate('time_validation_error'),
    }
  }

  return { isValid: true }
}

/**
 * Converts local date and time to UTC ISO string
 * @param date - Date in YYYY-MM-DD format
 * @param time - Time in HH:MM format
 * @returns UTC ISO string
 */
export const toUTCISOString = (date: string, time: string): string => {
  const localDateTime = new Date(`${date}T${time}:00`)
  return localDateTime.toISOString()
}

export const calculateDuration = (
  start: string,
  end: string,
  t?: TFunction
): string => {
  const startTime = new Date(start).getTime()
  const endTime = new Date(end).getTime()
  const durationMs = endTime - startTime

  const seconds = Math.floor((durationMs / 1000) % 60)
  const minutes = Math.floor((durationMs / (1000 * 60)) % 60)
  const hours = Math.floor((durationMs / (1000 * 60 * 60)) % 24)
  const days = Math.floor(durationMs / (1000 * 60 * 60 * 24))

  // Use provided translation function or fallback to i18n
  const translate =
    t ||
    ((key: string, options?: { count?: number }) =>
      i18n.t(`common:time.duration.${key}` as never, options))

  if (days > 0) {
    return `${translate('days', { count: days })} ${translate('hours', {
      count: hours,
    })} ${translate('minutes', { count: minutes })}`
  }
  if (hours > 0) {
    return `${translate('hours', { count: hours })} ${translate('minutes', {
      count: minutes,
    })}`
  }
  if (minutes > 0) {
    return `${translate('minutes', { count: minutes })} ${translate('seconds', {
      count: seconds,
    })}`
  }
  return translate('seconds', { count: seconds })
}